// secrets.mjs — 密递取件面的纯逻辑（第一百零九批 D）。无 React、无 DOM。
//
// ── 这一页为什么是本批的**前提**而不是附赠品 ─────────────────────────────────
// 后端的四只密递端点从第一百零六批就在，全是 require_session ⇒ 浏览器一直调得动；
// 而前端**零文件、i18n 零键**——取件这件事在页面上不存在，只有 CLI 做得到。
// 于是：CLI 要一枚 key 才能跑 → key 只能从密递取 → 密递只能用 CLI 取，
// **「用 key 才能取到 key」是个死锁**。裁定 1（API key 延迟领取 = 复用定向密递）
// 整条路能不能成立，就取决于这一页 + CLI 下载页两块都到位。
//
// ── 取件是全站唯一一处**破坏性的读**（风险 17）───────────────────────────────
// once 件点一下就没了。所以这个模块的每只函数都在服务同一件事：**在他点之前**就把
// "取走就没了"说清楚，并且让"这一件是哪一种"成为一个可判定的问题而不是一句提示。
//  · takeConfirmKey() 按 mode 给不同的确认文案 —— 一次性件说"取出就烧掉"，
//    限时件说"到 {t} 之前还能再取"。给同一句话是把两件后果完全不同的事说成一样。
//  · takeAction() 让"能不能取"在渲染前就有答案，而不是点下去让 403 来回答。
//
// ── once + ttl 是本批解禁的组合（契约 A.4）──────────────────────────────────
// 密递头注原本否决"既限时又即焚"，理由是「取件方分不清撞的是哪一种失败」。
// GONE_TAKEN / GONE_EXPIRED / GONE_REVOKED 三码 + 三句人话上线之后，那条理由已经
// **不成立**——规则的适用范围由它的理由划定。所以本模块必须能表达第三种形态：
// `mode==='once' && expiresAtMs != null` ⇒ secrets.mode.onceTtl。少了它，一件
// API key 密件会被显示成"一次性"而不说它 72 小时后就没了，收件人于是慢慢地拖过期。

export const SECRET_STATE_LABEL = {
  available: 'secrets.state.available',
  taken: 'secrets.state.taken',
  expired: 'secrets.state.expired',
  revoked: 'secrets.state.revoked',
};

// 配色：available 是**要人动手**的那一态（尤其 once 件的窗口有限），给它 success；
// 其余三态都是终局，一律灰——它们之间的差别由标签说，不由颜色说（三种灰色的
// 细微差别没有人分得出来，而三句话分得出来）。
export const SECRET_STATE_COLOR = {
  available: 'success',
  taken: 'default',
  expired: 'default',
  revoked: 'default',
};

// 错误码 → i18n 键（契约 D.7）。三种 GONE 各有一句人话，这正是本批敢解禁
// once+ttl 的那条理由的落点：取件方必须分得清自己撞的是哪一种失败，因为善后
// 完全不同（被取走 ⇒ 凭据可能外流，去轮换；过期/吊销 ⇒ 找签发方）。
export const SECRET_ERROR_KEYS = {
  GONE_TAKEN: 'secrets.err.goneTaken',
  GONE_EXPIRED: 'secrets.err.goneExpired',
  GONE_REVOKED: 'secrets.err.goneRevoked',
  ISSUER_CANNOT_TAKE: 'secrets.err.issuerCannotTake',
  ALREADY_REVOKED: 'secrets.err.alreadyRevoked',
};

// 时刻收敛。⚠ `Number(null)` 是 **0**（`Number('')` 也是），所以不能只靠
// Number.isFinite —— 一个"没有过期时间"的 null 会变成 1970-01-01，而那是一句
// 确定的假话（"这件密件早就过期了"）。null / undefined / 空串一律给 null。
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function stateLabelKey(state) {
  return Object.prototype.hasOwnProperty.call(SECRET_STATE_LABEL, state)
    ? SECRET_STATE_LABEL[state] : null;
}
export function stateColor(state) {
  return Object.prototype.hasOwnProperty.call(SECRET_STATE_COLOR, state)
    ? SECRET_STATE_COLOR[state] : 'default';
}

// 一件密件的受控形状。**这里没有 body，也不可能有**：后端的 SecretDeliveryRow 里
// 就没有那个字段，正文只从取件那一条路单独取一次（而且取了就可能没了）。
export function normalizeDelivery(r) {
  if (!r || typeof r !== 'object' || r.id == null) return null;
  return {
    id: String(r.id),
    label: typeof r.label === 'string' ? r.label : '',
    toGroup: typeof r.toGroup === 'string' ? r.toGroup : '',
    // null = 存量的组粒度行（第一百零八批 C3 之前签发的）。**不回落成一句
    // "全组"**：调用侧要按它分岔，而一个假的收件人名字会让人以为那件是给别人的。
    toAccount: typeof r.toAccount === 'string' && r.toAccount ? r.toAccount : null,
    // SB-147：稳定身份位（成员面不露整串邮箱——后端 person_display 口径，
    // 「邮箱（或 #id）」裁文里给这面的是 #id 那一支）。缺席 ⇒ null（旧响应/存量行）。
    toAccountId: num(r.toAccountId),
    fromId: num(r.fromId),
    isolation: r.isolation === 'account' ? 'account' : 'group',
    mode: typeof r.mode === 'string' ? r.mode : '',
    state: typeof r.state === 'string' ? r.state : '',
    expiresAtMs: num(r.expiresAtMs),
    createdAtMs: num(r.createdAtMs),
    fromDisplay: typeof r.fromDisplay === 'string' ? r.fromDisplay : '',
    viaAgent: r.viaAgent === true,
    readAtMs: num(r.readAtMs),
    revokedAtMs: num(r.revokedAtMs),
    mine: r.mine === true,
  };
}

export function normalizeDeliveries(data) {
  const rows = Array.isArray(data?.deliveries) ? data.deliveries : [];
  const out = [];
  for (const r of rows) {
    const n = normalizeDelivery(r);
    if (n) out.push(n);
  }
  // 顺序（id DESC）是后端的展示语义，前端不再排一次。
  return out;
}

// ── 三种形态的类型标签（头注：once+ttl 是本批解禁的第三种）──────────────────
export function modeText(row) {
  if (!row) return null;
  if (row.mode === 'once' && row.expiresAtMs != null) {
    return { key: 'secrets.mode.onceTtl', ms: row.expiresAtMs };
  }
  if (row.mode === 'once') return { key: 'secrets.mode.once', ms: null };
  if (row.mode === 'ttl') return { key: 'secrets.mode.ttl', ms: row.expiresAtMs };
  return null;                                  // 未知 mode ⇒ 印原始串，不猜
}

// 取件确认框的正文。**按 mode 分岔**：一次性件必须在点之前就说清"取走就没了、
// 这个窗口关掉也没了"；限时件说"到 {t} 之前还能再取"。给同一句话就是把两件后果
// 完全不同的事说成一样，而其中一件是不可逆的。
export function takeConfirmKey(row) {
  if (!row) return null;
  if (row.mode === 'once') {
    return { title: 'secrets.take.confirmTitleOnce', body: 'secrets.take.confirmBodyOnce',
             ms: row.expiresAtMs, burns: true };
  }
  // ⚠ 标题也必须分岔（第一百零九批处置轮 R16）。限时件**不焚**（store 只在
  // mode=='once' 时清空正文），而此前两支返回同一个 title 键，于是同一只对话框标题说
  // 「取出就烧掉」、正文说「到期之前可以反复取出」——自相矛盾。一句会被当场证伪的
  // 警告会训练用户不信这个标题，而它正是 once 件唯一那道"不可逆"的提示。
  return { title: 'secrets.take.confirmTitleTtl', body: 'secrets.take.confirmBodyTtl',
           ms: row.expiresAtMs, burns: false };
}

// ── SB-148 A：下载为 .txt（第一百二十四批）──────────────────────────────────
// 剪切板此前是明文离开这一页的唯一通道，而剪切板本身是个泄漏面（剪贴板管理器、
// 云同步、多设备历史）。这只函数给出第二条通道的**全部形状**——页面只拿它去装 Blob，
// 内容与文件名不许在 JSX 里二次拼装。两条硬规矩，各有逐字节判据：
//  · parts = [值本身]，**逐字节**：不加 BOM、不补尾随换行、不拼 label/元数据。
//    这个文件的唯一用途是"粘贴即用"，多一个字节就是一枚粘不上的 key（BOM 尤其
//    隐蔽：编辑器不显示它，粘进配置的 key 却多了三个字节）。元数据在文件名与
//    页面上，不进文件。
//  · 文件名 secret-<id>.txt：id 是清单上可见的编号，事后对得上"哪一件"；label
//    不进文件名——它是自由文本，路径分隔符和空格都是麻烦。
// 这里**不碰一次性语义**：body 是取件那一刻已经在内存里的那一份，下载不重取、
// 不缓存到组件外。
export function downloadSpec(row, body) {
  return {
    name: `secret-${row?.id ?? 'unknown'}.txt`,
    parts: [typeof body === 'string' ? body : ''],
  };
}

// ── 「这一件我能不能取」───────────────────────────────────────────────────
// 'take'       —— 可取（渲染取件按钮）
// 'issuerOnly' —— 我是签发方而不是收件人：他在自己的清单里看得见这一件（他发的），
//                 但取件是**收件方**的动作，服务端回 403 ISSUER_CANNOT_TAKE。
//                 与其让他点一下再被拒，不如把按钮换成那句解释——他手上本就有原件。
// 'none'       —— 终局（已取走/已过期/已吊销），没有可取的东西。
//
// 判据只做**显示**，闸永远在服务端。所以形状不明时（拿不到自己的显示名）一律回落
// 'take'：多按一次得到一句 403 的解释是无害的，而错误地隐藏取件按钮会让收件人以为
// 这件不是给他的——那才是让一枚 key 烂在库里的形状。
export function takeAction(row, account) {
  if (!row || row.state !== 'available') return 'none';
  if (row.mine && !addressedToMe(row, account)) return 'issuerOnly';
  return 'take';
}

// 「这件是指名给我的吗」。**首选比 id**（SB-147：显示名是可变的装饰，拿它当身份
// 正是三次错置事故的病根——本函数此前正是显示名串比对，判定层自己带病）。
// toAccountId 在 ⇒ 只认 id；缺席（部署错峰的旧响应/极旧行）才退回显示名比对，
// 失败方向不变：判不清 ⇒ takeAction 回落 'take'，多按一次换一句 403 解释无害。
// 存量的组粒度行（toAccount === null 且无 id）对组内每个人都成立 ⇒ true。
export function addressedToMe(row, account) {
  if (!row || (row.toAccount == null && row.toAccountId == null)) return true;
  if (row.toAccountId != null) {
    const mineId = Number(account?.id);
    return Number.isFinite(mineId) && mineId === row.toAccountId;
  }
  const mine = personDisplay(account);
  return mine !== '' && mine === row.toAccount;
}

export function personDisplay(account) {
  const nick = typeof account?.displayName === 'string' ? account.displayName.trim() : '';
  if (nick) return nick;
  const email = typeof account?.email === 'string' ? account.email : '';
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

// 吊销：签发方或管理员，且只在"还可取"的时候有意义——吊销一件已经被取走的密件
// 什么也保护不了（正文早就出去了），把按钮摆在那里只会让人以为还来得及。
export function mayRevoke(row, account) {
  if (!row || row.state !== 'available') return false;
  return row.mine === true || account?.isAdmin === true;
}
